import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import { Link as LinkButton } from '@fluentui/react';
import { GoalSelector, getStyles } from '@bingads-webui-campaign-react/goal-selector';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';

const adjustWidth = {
  brandName: {
    width: 180,
  },
  brandDomain: {
    width: 300,
  },
};

const PureBrandSelectorUnStyled = (props) => {
  const {
    i18n,
    i18nLegacy,
    classes,
    manuallyAddBrand,
    activity,
    logScope,
  } = props;

  const renderHeaderFactory = useCallback(() => (() => {
    const BrandName = i18n.getString(_TL_('Brand Name'));
    const BrandDomain = i18n.getString(_TL_('Brand Domain'));

    return (
      <div className={classes.rowItem}>
        <div className={classes.goalNameColumn} style={adjustWidth.brandName}>
          <b title={BrandName}>{BrandName}</b>
        </div>
        <div className={classes.goalTypeColumn} style={adjustWidth.brandDomain}>
          <b title={BrandDomain}>{BrandDomain}</b>
        </div>
      </div>
    );
  }), [classes, i18n]);

  const renderItemFactory = useCallback(({ isSelected }) => ((item, index, forwardedRef) => {
    const { isAddedToResultList } = item;
    const rowItemClass = isAddedToResultList ? 'added-to-result-list' : '';
    const addIconClass = isAddedToResultList ? 'disabled' : 'enabled';

    return (
      <div className={`brand-item ${rowItemClass} ${classes.rowItem}`}>
        <div
          className={`brand-name ${classes.goalNameColumn}`}
          ref={forwardedRef}
          style={adjustWidth.brandName}
          title={item.BrandName}
        >
          {item.BrandName}
        </div>
        <div className={`brand-domain ${classes.goalTypeColumn}`} style={adjustWidth.brandDomain} title={item.BrandUrl}>
          {item.BrandUrl}
        </div>
        {isSelected ?
          <span className={`arrow-button a11y-k-listview-button remove remove-arrow-button ${classes.arrowIcon}`} /> :
          <span className={`arrow-button a11y-k-listview-button add-arrow-button ${addIconClass} ${classes.arrowIcon}`} />
        }
      </div>
    );
  }), [classes]);

  const renderFooter = useCallback(() => (
    <div className={`${classes.manuallyAddBrand} manually-open-brand-button`}>
      <ReactTemplate
        model={{
          link: text => <LinkButton onClick={manuallyAddBrand}>{text}</LinkButton>,
        }}
        template={i18n.getString(_TL_('Not finding your brand? {{link}}Manually add{{/link}}'))}
      />
    </div>
  ), [i18n, manuallyAddBrand, classes]);

  const selectionLogger = useCallback((isRemove) => {
    activity.trace(`${logScope}|PureBrandSelector|${isRemove ? 'ItemRemoved' : 'ItemSelected'}`);
  }, [activity, logScope]);

  const newProps = useMemo(() => _.defaults({
    sourceGoalSelectorOptions: {
      renderHeader: renderHeaderFactory({ isSelected: false }),
      renderItem: renderItemFactory({ isSelected: false }),
      renderFooter,
      onSelect: () => selectionLogger(false),
      onSelectAll: () => selectionLogger(false),
    },
    resultContainerOptions: {
      isHideSaveBar: true,
      renderHeader: renderHeaderFactory({ isSelected: true }),
      renderItem: renderItemFactory({ isSelected: true }),
      onRemove: () => selectionLogger(true),
      onRemoveAll: () => selectionLogger(true),
    },
    i18n: i18nLegacy,
  }, props), [props, renderFooter, renderHeaderFactory, renderItemFactory, selectionLogger, i18nLegacy]);

  return (
    <div className={`brand-selector-container ${classes.goalSelectorContainer}`}>
      <div className={classes.goalSelector}>
        <GoalSelector {...newProps} />
      </div>
    </div>
  );
};

PureBrandSelectorUnStyled.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  i18nLegacy: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    goalSelectorContainer: PropTypes.string,
    goalSelector: PropTypes.string,
    rowItem: PropTypes.string,
    goalNameColumn: PropTypes.string,
    goalTypeColumn: PropTypes.string,
    arrowIcon: PropTypes.string,
    hide: PropTypes.string,
    manuallyAddBrand: PropTypes.string,
  }).isRequired,
  manuallyAddBrand: PropTypes.func.isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }).isRequired,
  logScope: PropTypes.string.isRequired,
};

const Wrapped = _.compose(
  memo,
  Comp => withDefaultStyles(Comp, getStyles)
)(PureBrandSelectorUnStyled);

export { Wrapped as PureBrandSelector };
